import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { api } from "@/lib/api";

// pixels-per-second (slightly faster on desktop, slower on mobile for readability)
const speedFor = (w) => (w < 640 ? 40 : 55);

export default function PromoBanner() {
  const [content, setContent] = useState(null);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem("promo_banner_dismissed") === "1"
  );
  const [paused, setPaused] = useState(false);
  const [duration, setDuration] = useState(20);
  const measureRef = useRef(null);

  useEffect(() => {
    api.get("/site-content").then((r) => setContent(r.data)).catch(() => setContent({}));
  }, []);

  // Build the list of enabled messages (fallback to legacy single text)
  const messages = React.useMemo(() => {
    if (!content) return [];
    const arr = Array.isArray(content.promo_banner_messages) ? content.promo_banner_messages : [];
    const enabled = arr.filter((m) => m && m.enabled && (m.text || "").trim()).map((m) => m.text.trim());
    if (enabled.length > 0) return enabled;
    if (content.promo_banner_text) return [content.promo_banner_text];
    return [];
  }, [content]);

  // Measure one copy of the message group and derive a readable, seamless animation duration
  useLayoutEffect(() => {
    if (!measureRef.current || messages.length === 0) return;
    const oneCopyWidth = measureRef.current.scrollWidth; // width of a single (repeated) half
    const pxPerSec = speedFor(window.innerWidth);
    const secs = Math.max(8, oneCopyWidth / pxPerSec);
    setDuration(secs);
  }, [messages, content]);

  if (dismissed || !content) return null;
  if (!content.promo_banner_enabled || messages.length === 0) return null;

  const bgColor = content.promo_banner_bg_color || "#B45309";
  const textColor = content.promo_banner_text_color || "#FFFFFF";

  const dismiss = () => {
    sessionStorage.setItem("promo_banner_dismissed", "1");
    setDismissed(true);
  };

  // Repeat the messages enough times that a single "half" comfortably exceeds the viewport,
  // then render that half twice so translateX(-50%) loops seamlessly.
  const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
  const approxCharWidth = 9;
  const groupChars = messages.join("      •      ").length + 6;
  const groupPx = Math.max(1, groupChars * approxCharWidth);
  const repeats = Math.max(1, Math.ceil((vw * 1.5) / groupPx));

  const Group = ({ aria }) => (
    <span className="inline-flex items-center" aria-hidden={aria || undefined}>
      {Array.from({ length: repeats }).flatMap((_, r) =>
        messages.map((m, i) => (
          <span key={`${r}-${i}`} className="inline-flex items-center">
            <span className="px-1 tracking-wide">{m}</span>
            <span className="px-4 opacity-60" aria-hidden>•</span>
          </span>
        ))
      )}
    </span>
  );

  return (
    <div
      data-testid="promo-banner"
      className="relative w-full overflow-hidden"
      style={{ backgroundColor: bgColor, color: textColor }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* hidden measurer: one half */}
      <div ref={measureRef} className="absolute opacity-0 pointer-events-none whitespace-nowrap text-xs sm:text-sm py-2 font-medium" aria-hidden>
        <Group />
      </div>

      <div
        className="promo-marquee-track text-xs sm:text-sm py-2 font-medium"
        data-testid="promo-marquee-track"
        style={{ animationDuration: `${duration}s`, animationPlayState: paused ? "paused" : "running" }}
      >
        <Group />
        <Group aria />
      </div>

      <button
        onClick={dismiss}
        data-testid="promo-banner-dismiss"
        aria-label="Dismiss banner"
        className="absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-black/20 transition-colors"
        style={{ backgroundColor: bgColor }}
      >
        <X className="w-4 h-4"/>
      </button>
    </div>
  );
}
